'use client'

import React, { useCallback, useState } from 'react'

import { Box, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { AlertTriangleIcon } from '../Icon/AlertTriangleIcon'
import { Spinner } from '../Spinner'
import {
  FileViewerSize,
  errorIconColor,
  zoomableImageContainerStyles,
  zoomableImageErrorStyles,
  zoomableImageSpinnerStyles,
  zoomableImageStyles,
} from './FileViewer.styles'

type ZoomableImageProps = {
  src: string
  name: string
  zoomLevel: number
  size: FileViewerSize
  colorScheme: ColorScheme
  isDragging: boolean
  containerRef: React.RefObject<HTMLDivElement | null>
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseUp: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => void
  onToggleSize: () => void
}

const getCursor = (
  zoomLevel: number,
  isDragging: boolean,
  size: FileViewerSize
) => {
  if (zoomLevel > 1) {
    return isDragging ? 'grabbing' : 'grab'
  }
  return size === 'normal' ? 'pointer' : 'default'
}

/**
 * Zoom/pan image surface for FileViewer. Port of the web-app's Chakra v1
 * ZoomableImage (ENG-32107): the Alert error state becomes AlertTriangleIcon +
 * text, the load Spinner and scrollbars go token-styled, behavior (cursor
 * affordances, click-to-fullscreen at normal size, width-based zoom scaling)
 * is unchanged.
 */
export const ZoomableImage = ({
  src,
  name,
  zoomLevel,
  size,
  colorScheme,
  isDragging,
  containerRef,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onMouseLeave,
  onToggleSize,
}: ZoomableImageProps) => {
  const [isImageLoading, setIsImageLoading] = useState(true)
  const [isImageError, setIsImageError] = useState(false)

  const handleImageLoad = useCallback(() => {
    setIsImageLoading(false)
  }, [])

  const handleImageError = useCallback(() => {
    setIsImageError(true)
    setIsImageLoading(false)
  }, [])

  const handleClick = useCallback(() => {
    if (!isDragging && size === 'normal') {
      onToggleSize()
    }
  }, [isDragging, size, onToggleSize])

  if (isImageError) {
    return (
      <Box {...zoomableImageErrorStyles(colorScheme)}>
        <AlertTriangleIcon color={errorIconColor(colorScheme)} size={20} />
        Failed to load image
      </Box>
    )
  }

  return (
    <Box
      cursor={getCursor(zoomLevel, isDragging, size)}
      onClick={handleClick}
      onMouseDown={onMouseDown}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      ref={containerRef}
      {...zoomableImageContainerStyles(colorScheme, zoomLevel > 1)}
    >
      {isImageLoading && (
        <Box {...zoomableImageSpinnerStyles}>
          <Spinner borderWidth='2px' colorScheme={colorScheme} />
        </Box>
      )}
      <chakra.img
        alt={name}
        onError={handleImageError}
        onLoad={handleImageLoad}
        src={src}
        {...zoomableImageStyles(zoomLevel)}
      />
    </Box>
  )
}
